import { Field, Label } from '@headlessui/react';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { CloseIcon } from '../../../assets/icons/index';
import MinusIcon from '../../../assets/icons/minus-icon';
import { Company, useModalState, VestingTaskHooks } from '../../../hooks/index';
import { ApprovalVestingTask } from '../../../types/vesting-tasks.types';
import {
  clampHurdleApprovalShares,
  getHurdleApprovalDefaultShares,
  getHurdleExercisableShares,
} from '../../../utils/hurdle-exercisable-shares.util';
import { toBackendDateFormat } from '../../../utils/to-backend-date-format.util';
import { AlertDialogWrapper } from '../../alert-dialog/index';
import { AppFormattedMessage } from '../../app-formatted-message/index';
import Button from '../../button/index';
import { Checkbox } from '../../checkbox/index';
import { Table } from '../../table/index';
import { HurdleTableRow } from './hurdle-table-row';
import { ApproveHurdleColumn, approveHurdleColumnMap, approveHurdleColumnTitle } from './types';

export const ApproveHurdlePlan: FC = () => {
  const {
    isOpen,
    toggler,
    handleCloseModal,
    paramsValue: requestId,
  } = useModalState('hurdleApproveId');
  const { create: exercise } = VestingTaskHooks.useExercise();

  const { selectedCompanyId: companyId } = Company.useSelected();

  const { approvalRequest, invalidateQuery } = VestingTaskHooks.useRequestApproveTasks({
    requestId,
    enabled: !!requestId,
  });

  const [selectedVestingTasks, setSelectedVestingTasks] = useState<ApprovalVestingTask[]>([]);
  const [approvalDates, setApprovalDates] = useState<Record<string, Date>>({});
  const [issuedSharesByTaskId, setIssuedSharesByTaskId] = useState<Record<string, string>>({});
  // Stable per-modal default so an untouched row keeps showing today rather than re-rendering a new
  // Date object on every keystroke elsewhere in the table.
  const [defaultApprovalDate] = useState(() => new Date());

  const onDeselectAll = () => setSelectedVestingTasks([]);

  const { valuation, vestingTasks = [] } = approvalRequest ?? {};

  // The backend already returns only approvable tasks, but guard here too so a row that has nothing
  // left to exercise can never be rendered or swept up by select-all.
  const approvableVestingTasks = vestingTasks.filter(
    (vestingTask) => getHurdleExercisableShares(vestingTask) > 0,
  );

  const onSelectAll = () => {
    setSelectedVestingTasks(approvableVestingTasks);
  };

  const getApprovalDate = (vestingTaskId: string) =>
    approvalDates[vestingTaskId] ?? defaultApprovalDate;

  const getIssuedSharesInput = (vestingTask: ApprovalVestingTask) =>
    issuedSharesByTaskId[vestingTask.id] ?? String(getHurdleApprovalDefaultShares(vestingTask));

  const getApprovalShares = (vestingTask: ApprovalVestingTask) =>
    clampHurdleApprovalShares(vestingTask, getIssuedSharesInput(vestingTask));

  const hasSelectedTaskWithoutShares = selectedVestingTasks.some(
    (vestingTask) => getApprovalShares(vestingTask) === 0,
  );

  if (!approvalRequest || !valuation) return;

  return (
    <AlertDialogWrapper control={{ onOpenChange: toggler, open: isOpen }}>
      <div className="flex w-[80%] flex-col gap-8 rounded-2xl bg-white p-8">
        <div className="flex w-full justify-between">
          <span className="text-xl font-semibold text-gray-700">
            <AppFormattedMessage id="APPROVE_HURDLE_PLAN" />
          </span>
          <Button
            className="centered flex size-7 rounded-full border border-gray-100 shadow-sm"
            onClick={handleCloseModal}
            styleType="NONE"
          >
            <CloseIcon />
          </Button>
        </div>
        <div className="max-h-[400px] w-full overflow-y-auto bg-white">
          <Table
            columnOrder={approveHurdleColumnMap}
            columnsTitle={{
              ...approveHurdleColumnTitle,
              [ApproveHurdleColumn.SELECT]: (
                <Field className="flex cursor-pointer items-center">
                  <Checkbox
                    checked={selectedVestingTasks.length > 0}
                    id="select-all-hurdle-plans"
                    onChange={(checked) => (checked ? onSelectAll() : onDeselectAll())}
                    selectIcon={<MinusIcon />}
                  />
                  <Label
                    className="absolute left-0 top-0 h-full w-full cursor-pointer"
                    htmlFor="select-all-hurdle-plans"
                  />
                </Field>
              ),
            }}
            tHeadClassNames={{ thClassName: 'pl-4' }}
          >
            {approvableVestingTasks.map((vestingTask) => (
              <HurdleTableRow
                approvalDate={getApprovalDate(vestingTask.id)}
                isSelected={selectedVestingTasks.some((vt) => vt.id === vestingTask.id)}
                issuedShares={getIssuedSharesInput(vestingTask)}
                key={vestingTask.id}
                onApprovalDateChange={(date) =>
                  setApprovalDates((prev) => ({ ...prev, [vestingTask.id]: date }))
                }
                onIssuedSharesChange={(value) =>
                  setIssuedSharesByTaskId((prev) => ({ ...prev, [vestingTask.id]: value }))
                }
                onRemoveSelect={(selectedId) =>
                  setSelectedVestingTasks((prev) => prev.filter((vt) => vt.id !== selectedId))
                }
                onSelect={(selectedId) => setSelectedVestingTasks((prev) => [...prev, selectedId])}
                valuation={valuation}
                vestingTask={vestingTask}
              />
            ))}
          </Table>
        </div>
        <div className="flex h-11 w-full justify-between overflow-x-auto text-sm">
          <Button
            className="w-fit font-[450] text-gray-700"
            onClick={handleCloseModal}
            styleType="NONE"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="w-[260px] font-[550] text-white"
            data-permission-required
            disabled={selectedVestingTasks.length === 0 || hasSelectedTaskWithoutShares}
            onClick={() =>
              exercise(
                {
                  companyId,
                  data: selectedVestingTasks.map((vestingTask) => ({
                    value: getApprovalShares(vestingTask),
                    vestingTaskId: vestingTask.id,
                    date: toBackendDateFormat(getApprovalDate(vestingTask.id)),
                  })),
                },
                {
                  onSuccess: () => {
                    toast.success(<AppFormattedMessage id="EXERCISED_SUCCESSFULLY" />);
                    invalidateQuery();
                    onDeselectAll();
                    toggler(false);
                  },
                },
              )
            }
          >
            <AppFormattedMessage id="APPROVE_HURDLES" />
          </Button>
        </div>
      </div>
    </AlertDialogWrapper>
  );
};
